"use strict";
/**
 * String helpers — slugs, order numbers, masking and safe truncation.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.slugify = slugify;
exports.uniqueSlug = uniqueSlug;
exports.formatOrderNumber = formatOrderNumber;
exports.deriveBranchPrefix = deriveBranchPrefix;
exports.truncate = truncate;
exports.escapeMarkdownV2 = escapeMarkdownV2;
exports.escapeHtml = escapeHtml;
exports.normalizeCode = normalizeCode;
exports.initials = initials;
/** Cyrillic → Latin, so a Russian/Uzbek-Cyrillic name still yields a usable slug. */
const CYRILLIC_MAP = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'j', з: 'z',
    и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
    с: 's', т: 't', у: 'u', ф: 'f', х: 'x', ц: 'ts', ч: 'ch', ш: 'sh',
    щ: 'sch', ъ: '', ы: 'i', ь: '', э: 'e', ю: 'yu', я: 'ya',
    ў: 'o', қ: 'q', ғ: 'g', ҳ: 'h',
};
/**
 * URL-safe slug: `"Chilonzor filiali"` → `"chilonzor-filiali"`.
 * Uniqueness is the caller's job — see {@link uniqueSlug}.
 */
function slugify(input) {
    return input
        .toLowerCase()
        .trim()
        .split('')
        .map((char) => CYRILLIC_MAP[char] ?? char)
        .join('')
        .normalize('NFD')
        // Strip combining accents left by NFD.
        .replace(/[̀-ͯ]/g, '')
        .replace(/['’`]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
}
/**
 * Appends a numeric suffix until the slug is free.
 * `existing` is the set of slugs already taken in the same scope.
 */
function uniqueSlug(base, existing) {
    const slug = slugify(base) || 'item';
    if (!existing.has(slug))
        return slug;
    let suffix = 2;
    while (existing.has(`${slug}-${suffix}`))
        suffix += 1;
    return `${slug}-${suffix}`;
}
/**
 * Builds the human-readable order label (TZ §41).
 *
 * With a branch prefix: `CH-1054`. Without one: `#1054`.
 */
function formatOrderNumber(number, branchPrefix) {
    return branchPrefix ? `${branchPrefix}-${number}` : `#${number}`;
}
/**
 * Derives a short branch prefix from its name, e.g. `"Chilonzor"` → `"CH"`.
 * Falls back to the first two alphanumeric characters, upper-cased.
 */
function deriveBranchPrefix(branchName, length = 2) {
    const latin = slugify(branchName).replace(/-/g, '');
    return (latin.slice(0, length) || 'BR').toUpperCase();
}
/** Truncates with an ellipsis, never cutting mid-surrogate. */
function truncate(input, maxLength) {
    if (input.length <= maxLength)
        return input;
    return `${Array.from(input).slice(0, Math.max(0, maxLength - 1)).join('')}…`;
}
/**
 * Escapes text for Telegram's MarkdownV2 parse mode.
 *
 * Order comments and customer names are attacker-controlled: an unescaped `_`
 * in a name breaks the whole notification message.
 */
function escapeMarkdownV2(input) {
    return input.replace(/[_*[\]()~`>#+\-=|{}.!\\]/g, (char) => `\\${char}`);
}
/** Escapes text for Telegram's HTML parse mode. */
function escapeHtml(input) {
    return input
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** Case-insensitive, whitespace-tolerant comparison for promo codes etc. */
function normalizeCode(input) {
    return input.trim().toUpperCase().replace(/\s+/g, '');
}
/** Initials for an avatar placeholder: `"Elbek Azizov"` → `"EA"`. */
function initials(fullName) {
    return fullName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
}
//# sourceMappingURL=strings.js.map